import { get } from "lodash/fp/object";
import { parse, stringify } from "qs";

/**
 * Parses a query string
 * @param queryString
 * @return The parsed oject
 */
export function getQSParams(queryString: string): Record<string, unknown> {
    return parse(queryString, { ignoreQueryPrefix: true });
}

/**
 * Merges a params object into an existing queryString
 * @param queryString Current query string
 * @param newParams  params to merge to QS
 * @return Updated query string
 */
export function updateQSParams(queryString: string, newParams: Record<string, unknown>): string {
    const oldParams = getQSParams(queryString);
    const params = Object.assign(oldParams, newParams);

    return `?${stringify(params)}`;
}

/**
 * Extract a parameters from url querystring.
 * @param queryString Query string from your url
 * @return The extracted parameters.
 */
export function getQSParamByName(queryString: string, name: string): unknown {
    if (!queryString || queryString === "") {
        throw new Error(
            "[getQSParamByName] you have to provide the query string to get url params"
        );
    }
    if (!name || name === "") {
        throw new Error(
            "[getQSParamByName] missing property name, provide it to get the url params you want to extract"
        );
    }

    return getQSParams(queryString)[name];
}

/**
 * Makes sure a given value is a boolean, either because it is or because it is
 * a string that can be casted to a boolean.
 * Throws an error if not possible.
 * @param value Value to be checked
 * @return Boolean or throws an error
 */
export function _enforceBooleanQSParam(value: unknown): boolean {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value !== "string") {
        throw new TypeError(
            "[_enforceBooleanUrlParam] expected boolean or something that can be converted to boolean"
        );
    } else {
        value = JSON.parse(value);
        if (typeof value !== "boolean") {
            throw new TypeError(
                "[_enforceBooleanUrlParam] expected boolean or something that can be converted to boolean"
            );
        } else {
            return value;
        }
    }
}

/**
 * Extracts a desired boolean parameter from a query string. If the property is
 * not defined within the query string it returns a default bool.
 * @param queryString Query string to be parsed
 * @param paramName Name of the boolean property to be parsed
 * @param defaultBool Bool to return in case the property s not defined
 * @return Boolean value or throws an error
 */
export function getBooleanQSParam(
    queryString: string,
    paramName: string,
    defaultBool = false
): boolean {
    const params = getQSParams(queryString);
    const booleanParam = get(paramName, params);

    return booleanParam ? _enforceBooleanQSParam(booleanParam) : defaultBool;
}
